import axios from './api';

export interface AdminCellField<T = number | null> {
	value: T;
	confidence: number;
	/** Plain-language reason the resolver left this field empty, for the reviewer. */
	reviewMessage?: string;
}

export interface AdminCellResolved {
	projectId: AdminCellField;
	precinctId: AdminCellField;
	blockId: AdminCellField;
	clientId: AdminCellField;
	typeId: AdminCellField;
	subTypeId: AdminCellField;
	subTypeValueId: AdminCellField;
	level: AdminCellField;
	unit: AdminCellField;
	userId: AdminCellField;
	statusType: AdminCellField<number>;
	quantity?: AdminCellField<number | null>;
	quantityUnit?: AdminCellField<string | null>;
	performedAt?: AdminCellField<string | null>;
	workCategory?: AdminCellField<WorkCategory | null>;
	isClientBillable?: AdminCellField<boolean | null>;
	isWorkerCreditable?: AdminCellField<boolean | null>;
	reportedLocationText?: AdminCellField<string | null>;
}

export interface AdminCellLabels {
	projectName: string | null;
	clientName: string | null;
	precinctName: string | null;
	blockNumber: string | null;
	blockLevelFrom: number | null;
	blockLevelTo: number | null;
	blockUnitFrom: number | null;
	blockUnitTo: number | null;
	blockUnitNumberPattern: 'continuous' | 'odd' | 'even' | null;
	typeName: string | null;
	subTypeName: string | null;
	subTypeValue: string | null;
	senderName: string | null;
}

export interface ExtractionLogCell {
	cellId: string;
	rawLine: string | null;
	flags: string[];
	crossCheckIssues: string[];
	writeable: boolean;
	resolved: AdminCellResolved;
	labels: AdminCellLabels;
	coWorkers: { id: number; name: string }[];
	quantity?: number | null;
	quantityUnit?: string | null;
	performedAt?: string | null;
	workCategory?: WorkCategory | null;
	isClientBillable?: boolean | null;
	isWorkerCreditable?: boolean | null;
	reportedLocationText?: string | null;
	participants?: WorkDoneParticipantInput[];
	reviewStatus?: ReviewStatus;
}

export type WorkCategory = 'normal' | 'free_redo' | 'rectify' | 'fabrication';
export type ReviewStatus = 'pending' | 'approved' | 'rejected';
export type ParticipantRole = 'primary' | 'helper';

export interface WorkDoneParticipantInput {
	userId: number;
	role: ParticipantRole;
	isCredited: boolean;
	name?: string;
}

export interface MessageEvidence {
	id: number;
	type: 'image';
	mimeType: string;
	fileName: string | null;
	byteSize: number;
	sha256: string;
	source: 'whatsapp' | 'admin';
	version: number;
	createdAt: string;
}

export type MessageType = 'work-done' | 'informational' | 'material-log' | 'inspection' | 'status-update' | 'unknown';

export type StatusFilter = 'pending' | 'done' | 'all';
export type MessageTypeFilter = 'all' | MessageType;

export interface ExtractionLogRow {
	id: number;
	messageId: string;
	createdAt: string;
	messageTimestamp?: string | null;
	rawMessage: string;
	flags: string[] | null;
	crossCheckIssues: string[] | null;
	status: 'pending' | 'done';
	senderName: string | null;
	fromPhone: string;
	groupJid: string;
	resolutionSource: 'tier1' | 'ai-fallback' | 'admin';
	aiConfidence: number | null;
	aiReasoning: string | null;
	messageType: MessageType;
	createdWorkDoneIds: number[] | null;
	cells: ExtractionLogCell[];
	evidence?: MessageEvidence[] | null;
	mediaJson?: {
		type?: string | null;
		mimetype?: string | null;
		fileName?: string | null;
		fileLength?: number | null;
		width?: number | null;
		height?: number | null;
		seconds?: number | null;
		viewOnce?: boolean;
	} | null;
	groupRole?: string | null;
	evidenceOnly?: boolean;
	reviewStatus?: ReviewStatus;
}

export interface ResolveCellPayload {
	cellId: string;
	userId: number;
	projectId: number;
	clientId: number;
	precinctId: number | null;
	typeId: number;
	subTypeId: number;
	subTypeValueId: number;
	blockId: number | null;
	level: number | null;
	unit: number | null;
	statusType: 0 | 1;
	quantity: number;
	quantityUnit: string;
	performedAt: string;
	workCategory: WorkCategory;
	isClientBillable: boolean;
	isWorkerCreditable: boolean;
	helperUserIds: number[];
	reportedLocationText: string | null;
}

export interface ResolveResult {
	created: number[];
	skipped: Array<{
		cellIndex: number;
		level: number;
		unit: number;
		reason: string;
	}>;
	logStatus: 'done';
}

export const listPendingMessages = async (
	page: number,
	limit: number,
	status: StatusFilter = 'pending',
	messageType: MessageTypeFilter = 'all'
) => {
	const params = new URLSearchParams({
		page: String(page),
		limit: String(limit),
		status,
		messageType,
	});
	const response = await axios.get('extractionLog/get-pending?' + params.toString());
	return response.data;
};

export const markMessageHandled = async (id: number) => {
	const response = await axios.put('extractionLog/mark-handled?id=' + id);
	return response.data;
};

export type RerunResult =
	| { state: 'completed'; workDoneIds: number[] }
	| { state: 'needs_review'; parserOnly?: boolean }
	| { state: 'failed'; error: string };

/** Replay selected Needs-review messages through the current parser and AI. */
export const startMessageRerun = async (
	ids: number[]
): Promise<{
	success: boolean;
	message?: string;
	data?: { accepted: number[]; skipped: Array<{ id: number; reason: string }> };
}> => {
	const response = await axios.post('extractionLog/rerun', { ids });
	return response.data;
};

export const getMessageRerunStatus = async (
	ids: number[]
): Promise<{ success: boolean; data: { running: number[]; results: Record<number, RerunResult> } }> => {
	const response = await axios.get('extractionLog/rerun-status?ids=' + ids.join(','));
	return response.data;
};

export const resolveExtractionLog = async (id: number, cells: ResolveCellPayload[]) => {
	const response = await axios.post('extractionLog/' + id + '/resolve', { cells });
	return response.data;
};

export const fetchMessageEvidenceBlob = async (evidenceId: number): Promise<Blob> => {
	const response = await axios.get(`extractionLog/evidence/${evidenceId}/content`, {
		responseType: 'blob',
	});
	return response.data;
};

export const uploadMessageEvidence = async (logId: number, image: File, replacesEvidenceId?: number) => {
	const form = new FormData();
	form.append('image', image);
	if (replacesEvidenceId != null) form.append('replacesEvidenceId', String(replacesEvidenceId));
	const response = await axios.post(`extractionLog/${logId}/evidence`, form);
	return response.data;
};
